import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";

export interface DocumentResponse {
  id: UUID;
  company_id: UUID;
  name: string;
  url: string;
  document_type: string;
  reference_type: string;
  reference_id: UUID;
  document_metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface DocumentCreate {
  name: string;
  url: string;
  document_type: string;
  reference_type: string;
  reference_id: UUID;
  document_metadata?: Record<string, unknown>;
}

export interface DocumentUpdate {
  name?: string;
  url?: string;
  document_type?: string;
  document_metadata?: Record<string, unknown>;
}

export interface DocumentFilters {
  reference_type?: string;
  reference_id?: UUID;
  document_type?: string;
}

export default class APIDocuments extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  list = async (filters?: DocumentFilters): Promise<DocumentResponse[]> => {
    const params = new URLSearchParams();
    if (filters?.reference_type)
      params.append("reference_type", filters.reference_type);
    if (filters?.reference_id)
      params.append("reference_id", filters.reference_id);
    if (filters?.document_type)
      params.append("document_type", filters.document_type);

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/documents/${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  get = async (id: UUID): Promise<DocumentResponse> => {
    const res = await this._fetch({
      url: `/documents/${id}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getByReference = async (
    referenceType: string,
    referenceId: UUID,
  ): Promise<DocumentResponse[]> => {
    const params = new URLSearchParams();
    params.append("reference_type", referenceType);
    params.append("reference_id", referenceId);

    const res = await this._fetch({
      url: `/documents/by-reference?${params.toString()}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  create = async (data: DocumentCreate): Promise<DocumentResponse> => {
    const res = await this._fetch({
      url: `/documents/`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  update = async (
    id: UUID,
    data: DocumentUpdate,
  ): Promise<DocumentResponse> => {
    const res = await this._fetch({
      url: `/documents/${id}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  delete = async (id: UUID): Promise<void> => {
    await this._fetch({
      url: `/documents/${id}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  deleteByReference = async (
    referenceType: string,
    referenceId: UUID,
  ): Promise<{ deleted_count: number }> => {
    const params = new URLSearchParams();
    params.append("reference_type", referenceType);
    params.append("reference_id", referenceId);

    const res = await this._fetch({
      url: `/documents/by-reference?${params.toString()}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
